import React, { useState, useEffect } from 'react';
import {
  Box, Typography, IconButton, Chip, Grid, TableRow, TableCell,
  DialogContent, DialogActions, TextField, Alert, Tooltip, useTheme
} from '@mui/material';
import {
  Add, Edit, Delete, Block, CheckCircle, Sell
} from '@mui/icons-material';
import api from '../../utils/api';
import {
  SectionHeader, GlassTable, GlassDialog,
  GradientButton, EmptyState, ShimmerTableRows
} from './ui';
import { glass } from './ui/glassTokens';

function SalesUsersManagement() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const g = glass(theme);

  const [salesUsers, setSalesUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Form states
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    city: '',
    age: ''
  });

  useEffect(() => {
    fetchSalesUsers();
  }, []);

  const fetchSalesUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/sales-users');
      setSalesUsers(response.data.data.users || []);
    } catch (err) {
      setError('Failed to load sales users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.name || !formData.email || !formData.password) {
        setError('Name, email, and password are required');
        return;
      }

      await api.post('/admin/sales-users', formData);
      setSuccess('Sales user created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchSalesUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create sales user');
    }
  };

  const handleEdit = async () => {
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }

      await api.put(`/admin/sales-users/${selectedUser.id}`, updateData);
      setSuccess('Sales user updated successfully');
      setEditDialogOpen(false);
      setSelectedUser(null);
      resetForm();
      fetchSalesUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update sales user');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/admin/sales-users/${selectedUser.id}`);
      setSuccess('Sales user deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      fetchSalesUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete sales user');
    }
  };

  const handleToggleBlock = async (user) => {
    try {
      await api.put(`/admin/sales-users/${user.id}/block`);
      setSuccess(`Sales user ${user.blocked ? 'unblocked' : 'blocked'} successfully`);
      fetchSalesUsers();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user status');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      phone: user.phone || '',
      city: user.city || '',
      age: user.age || ''
    });
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      city: '',
      age: ''
    });
  };

  const closeAllDialogs = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setDeleteDialogOpen(false);
    setSelectedUser(null);
    resetForm();
  };

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      ...g.surface,
      '& fieldset': { border: 'none' },
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)',
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(15,23,42,0.5)',
    },
  };

  const userFormFields = (isCreate) => (
    <Grid container spacing={2} sx={{ mt: 1 }}>
      <Grid item xs={12}>
        <TextField fullWidth label="Name *" value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })} sx={fieldSx} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth label="Email *" type="email" value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })} sx={fieldSx} />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label={isCreate ? 'Password *' : 'New Password (leave empty to keep current)'}
          type="password"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          sx={fieldSx}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Phone" value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })} sx={fieldSx} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Age" type="number" value={formData.age}
          onChange={(e) => setFormData({ ...formData, age: e.target.value })} sx={fieldSx} />
      </Grid>
      <Grid item xs={12}>
        <TextField fullWidth label="City" value={formData.city}
          onChange={(e) => setFormData({ ...formData, city: e.target.value })} sx={fieldSx} />
      </Grid>
    </Grid>
  );

  return (
    <Box>
      <SectionHeader
        eyebrow="Team"
        title="Sales Users"
        action={
          <GradientButton variant="primary" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
            Add Sales User
          </GradientButton>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <GlassTable
        columns={[
          { label: 'Name' },
          { label: 'Email' },
          { label: 'Phone' },
          { label: 'City' },
          { label: 'Status' },
          { label: 'Created' },
          { label: 'Actions', align: 'right' },
        ]}
      >
        {loading ? (
          <ShimmerTableRows count={4} cols={7} />
        ) : salesUsers.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} sx={{ border: 0, p: 0 }}>
              <EmptyState
                icon={<Sell />}
                iconIndex={0}
                title="No sales users yet"
                description="Create a sales user to manage libraries and institutes."
                action={
                  <GradientButton variant="primary" startIcon={<Add />} onClick={() => setCreateDialogOpen(true)}>
                    Add Sales User
                  </GradientButton>
                }
              />
            </TableCell>
          </TableRow>
        ) : (
          salesUsers.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                <Typography variant="body2" fontWeight="600" sx={{ color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(15,23,42,0.85)' }}>
                  {user.name}
                </Typography>
              </TableCell>
              <TableCell sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)', fontSize: '0.85rem' }}>
                {user.email}
              </TableCell>
              <TableCell sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)', fontSize: '0.85rem' }}>
                {user.phone || '-'}
              </TableCell>
              <TableCell sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(15,23,42,0.6)', fontSize: '0.85rem' }}>
                {user.city || '-'}
              </TableCell>
              <TableCell>
                <Chip
                  label={user.blocked ? 'Blocked' : 'Active'}
                  size="small"
                  sx={{
                    fontSize: '0.7rem', fontWeight: 600, height: 22, border: 'none',
                    background: user.blocked ? 'rgba(244,63,94,0.15)' : 'rgba(16,185,129,0.15)',
                    color: user.blocked ? '#F43F5E' : '#10B981',
                  }}
                />
              </TableCell>
              <TableCell sx={{ fontSize: '0.8rem', color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                {new Date(user.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell align="right">
                <Tooltip title="Edit">
                  <IconButton size="small" onClick={() => openEditDialog(user)}
                    sx={{ color: isDark ? 'rgba(255,255,255,0.55)' : 'rgba(15,23,42,0.55)' }}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={user.blocked ? 'Unblock' : 'Block'}>
                  <IconButton size="small" onClick={() => handleToggleBlock(user)}
                    sx={{ color: user.blocked ? '#10B981' : '#F59E0B' }}>
                    {user.blocked ? <CheckCircle fontSize="small" /> : <Block fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => openDeleteDialog(user)} sx={{ color: '#F43F5E' }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))
        )}
      </GlassTable>

      {/* Create Dialog */}
      <GlassDialog open={createDialogOpen} onClose={closeAllDialogs} title="Create Sales User" maxWidth="sm" fullWidth>
        <DialogContent>{userFormFields(true)}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={closeAllDialogs}>Cancel</GradientButton>
          <GradientButton variant="primary" onClick={handleCreate}>Create</GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* Edit Dialog */}
      <GlassDialog open={editDialogOpen} onClose={closeAllDialogs} title="Edit Sales User" maxWidth="sm" fullWidth>
        <DialogContent>{userFormFields(false)}</DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={closeAllDialogs}>Cancel</GradientButton>
          <GradientButton variant="primary" onClick={handleEdit}>Update</GradientButton>
        </DialogActions>
      </GlassDialog>

      {/* Delete Dialog */}
      <GlassDialog open={deleteDialogOpen} onClose={closeAllDialogs} title="Delete Sales User" maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(15,23,42,0.7)' }}>
            Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <GradientButton variant="ghost" onClick={closeAllDialogs}>Cancel</GradientButton>
          <GradientButton variant="danger" onClick={handleDelete}>Delete</GradientButton>
        </DialogActions>
      </GlassDialog>
    </Box>
  );
}

export default SalesUsersManagement;
